import {Direction} from '@angular/cdk/bidi';
import {booleanAttribute, computed, Directive, HostBinding, inject, Input, input, Signal, TemplateRef, ViewContainerRef} from '@angular/core';
import {Language, LanguageService} from '@core/language';

import ILanguage = Language.ILanguage;

@Directive({
	selector: '[dc-dir]',
	exportAs: 'dcDir',
	standalone: true,
})
export class DirectionDirective {
	/* --------------------------------  Public Signals (for exported directive) -------------------------------- */
	public readonly currentDir: Signal<Direction> = computed(() => this.languageService.currentLangData().dir);
	public readonly isRtl$: Signal<boolean> = computed(() => this.languageService.currentLangData().dir === 'rtl');
	public readonly isLtr$: Signal<boolean> = computed(() => this.languageService.currentLangData().dir === 'ltr');
	public readonly currentLang: Signal<ILanguage> = computed(() => this.languageService.currentLangData());
	/* -------------------------------------------------------------------------------------------------------------- */
	readonly rtlStyles = input<Record<string, string>>(undefined);
	readonly ltrStyles = input<Record<string, string>>(undefined);

	readonly rtlClass = input<string>(undefined);
	readonly ltrClass = input<string>(undefined);

	readonly setDirClass = input(true, {transform: booleanAttribute});

	readonly autoSetDir = input(false, {transform: booleanAttribute});

	constructor(private languageService: LanguageService = inject(LanguageService)) {}

	@HostBinding('class.rtl')
	protected get _rtl() {
		return this.setDirClass() ? this.languageService.currentLangData().dir === 'rtl' : '';
	}

	@HostBinding('class.ltr')
	protected get _ltr() {
		return this.setDirClass() ? this.languageService.currentLangData().dir === 'ltr' : '';
	}

	@HostBinding('style.direction') get _direction() {
		return this.setDirClass() ? this.languageService.currentLangData().dir : '';
	}

	@HostBinding('dir')
	protected get _dir() {
		return this.autoSetDir() ? this.languageService.currentLangData().dir : '';
	}

	@HostBinding('class')
	protected get _class() {
		const currentDir = this.languageService.currentLangData().dir;
		const rtlClass = this.rtlClass();
		const ltrClass = this.ltrClass();
		if (rtlClass && currentDir === 'rtl') return rtlClass;
		else if (ltrClass && currentDir === 'ltr') return ltrClass;
		else return '';
	}

	@HostBinding('style')
	protected get _style() {
		const currentDir = this.languageService.currentLangData().dir;
		return currentDir === 'rtl' ? this.rtlStyles() : this.ltrStyles();
	}
}

/* -------------------------------------------------------------------------------------------------------------- */
@Directive({
	selector: '[dcDir]',
	exportAs: 'dcDir',
	standalone: true,
})
export class DirectionConditionDirective {
	private readonly viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
	private readonly templateRef: TemplateRef<any> = inject(TemplateRef);
	private readonly languageService: LanguageService = inject(LanguageService);

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input({required: true}) set dcDir(condition: Partial<{rtl: boolean; ltr: boolean}>) {
		if (!condition) {
			this.viewContainerRef.createEmbeddedView(this.templateRef);
			return;
		}
		if (condition.rtl && this.languageService.currentLangData().dir === 'rtl') this.viewContainerRef.createEmbeddedView(this.templateRef);
		if (condition.ltr && this.languageService.currentLangData().dir === 'ltr') this.viewContainerRef.createEmbeddedView(this.templateRef);
	}
}
